/** @jsxImportSource @emotion/react */
'use memo'
import type { CSSProperties } from 'react'
import css from '@emotion/css/macro'
import { Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DollarOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { getBillingTypeLabel } from '@sportninja/common/utils/billing'
import Text from 'src/components/ts-components/Text'
import dayjs from 'dayjs'
import { formatCurrency } from '@sportninja/common/utils/utils'
import { formatShortDate } from '@sportninja/common/ts-utils/date-utils'
import { BillingScheduleItem } from '@sportninja/common/types/Registration'
import LoadingSpinner from 'src/components/LoadingSpinner'
import useWindowSize from 'src/hooks/useWindowSize'
import { getTableStyles } from 'src/styles/tableStyles'
import { useBillingContext } from '../context/BillingContext'
import { useUIContext } from '../context/UIContext'
import { useBillingActions } from '../hooks/useBillingActions'

const tooltipContentStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 250px;
`

// Static Tooltip overlay styles, hoisted so they aren't recreated per render
const tooltipOverlayStyle: CSSProperties = { maxWidth: 360 }
const tooltipInnerStyle: CSSProperties = { wordBreak: 'break-word' }

// TODO: Re-enable when email invoice button is restored
// const EmailReceiptIcon = () => (
//   <svg
//     xmlns='http://www.w3.org/2000/svg'
//     height='24px'
//     viewBox='0 -960 960 960'
//     width='24px'
//     fill={colors.PRIMARY}
//   >
//     <path d='m720-160-56-56 63-64H560v-80h167l-63-64 56-56 160 160-160 160ZM160-280q-33 0-56.5-23.5T80-360v-400q0-33 23.5-56.5T160-840h520q33 0 56.5 23.5T760-760v204q-10-2-20-3t-20-1q-10 0-20 .5t-20 2.5v-147L416-520 160-703v343h323q-2 10-2.5 20t-.5 20q0 10 1 20t3 20H160Zm58-480 198 142 204-142H218Zm-58 400v-400 400Z' />
//   </svg>
// )

const DownloadReceiptIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    height='24px'
    viewBox='0 -960 960 960'
    width='24px'
    fill={colors.PRIMARY}
  >
    <path d='m720-120 160-160-56-56-64 64v-167h-80v167l-64-64-56 56 160 160ZM560 0v-80h320V0H560ZM240-160q-33 0-56.5-23.5T160-240v-560q0-33 23.5-56.5T240-880h280l240 240v121h-80v-81H480v-200H240v560h240v80H240Zm0-80v-560 560Z' />
  </svg>
)

interface BillingTableProps {
  shouldDisplayBillingPayment: boolean
}

const BillingTable = ({ shouldDisplayBillingPayment }: BillingTableProps) => {
  const { width } = useWindowSize()
  const isMobile = width < 768
  const { billingItems, currencyCode } = useBillingContext()
  const { pdfLoadingIds } = useUIContext()
  const { handleDownloadPdf, handlePayment } = useBillingActions()

  const cols: ColumnsType<BillingScheduleItem> = [
    {
      title: 'Type',
      dataIndex: 'payment_type',
      key: 'payment_type',
      width: '100px',
      ellipsis: true,
      render: (_text: string, record: BillingScheduleItem) => (
        <span>{getBillingTypeLabel(record)}</span>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      align: 'center',
      width: isMobile ? 100 : 130,
      render: (_value: unknown, record: BillingScheduleItem) => {
        const isPaid = record.paid
        const isPastDue =
          dayjs(record.due_date).isBefore(dayjs()) && !record.paid
        const isRefunded = record?.charge?.refunded
        const isFailure = record.status_message !== null && record.failures > 0
        const isPaidInPerson = record.paid && record.paid_in_person

        if (isRefunded) {
          const isPartialRefund =
            record?.charge?.refund_amount &&
            record?.subtotal &&
            parseFloat(record?.charge?.refund_amount.toString()) <
              record?.subtotal

          return (
            <Tooltip
              title={
                <div css={tooltipContentStyles}>
                  <div>
                    <Text
                      variant='paragraphXSmall'
                      weight='semiBold'
                      color={colors.WHITE}
                    >
                      Reason:
                    </Text>{' '}
                    <Text
                      variant='paragraphSmall'
                      weight='regular'
                      color={colors.WHITE}
                    >
                      {record?.charge?.refund_reason || 'No reason provided'}
                    </Text>
                  </div>
                  {record?.charge?.refund_note && (
                    <div>
                      <Text
                        variant='paragraphXSmall'
                        weight='semiBold'
                        color={colors.WHITE}
                      >
                        Note:
                      </Text>{' '}
                      <Text
                        variant='paragraphSmall'
                        weight='regular'
                        color={colors.WHITE}
                      >
                        {record.charge.refund_note}
                      </Text>
                    </div>
                  )}
                  {record?.charge?.refund_amount && (
                    <div>
                      <Text
                        variant='paragraphXSmall'
                        weight='semiBold'
                        color={colors.WHITE}
                      >
                        Refund Amount:
                      </Text>{' '}
                      <Text
                        variant='paragraphSmall'
                        weight='regular'
                        color={colors.WHITE}
                      >
                        {formatCurrency(
                          record.charge.refund_amount,
                          currencyCode
                        )}
                      </Text>
                    </div>
                  )}
                </div>
              }
              placement='top'
              zIndex={99999}
              trigger={['hover']}
              mouseEnterDelay={0.5}
              overlayStyle={tooltipOverlayStyle}
              overlayInnerStyle={tooltipInnerStyle}
            >
              <span
                css={css`
                  padding: 2px 4px;
                  border-radius: 4px;
                  border: 1px solid ${colors.INFO_500};
                  background-color: rgba(6, 22, 51, 0.5);
                  color: ${colors.INFO_200};
                  font-size: ${isMobile ? '12px' : '14px'};
                  white-space: nowrap;
                  display: inline-block;
                  text-align: center;
                  min-width: 70px;
                  cursor: help;
                `}
              >
                {isPartialRefund ? 'Partial Refund' : 'Refunded'}
              </span>
            </Tooltip>
          )
        }

        if (isFailure) {
          return (
            <Tooltip
              title={
                <div css={tooltipContentStyles}>
                  <div>
                    <Text
                      variant='paragraphSmall'
                      weight='regular'
                      color={colors.WHITE}
                    >
                      {record.status_message}
                    </Text>
                  </div>
                  <div>
                    <Text
                      variant='paragraphSmall'
                      weight='regular'
                      color={colors.WHITE}
                    >
                      ({record.failures} Attempt
                      {record.failures > 1 ? 's' : ''})
                    </Text>
                  </div>
                </div>
              }
              placement='top'
              zIndex={99999}
              trigger={['hover']}
              mouseEnterDelay={0.5}
              overlayStyle={tooltipOverlayStyle}
              overlayInnerStyle={tooltipInnerStyle}
            >
              <span
                css={css`
                  padding: 2px 4px;
                  border-radius: 4px;
                  border: 1px solid ${colors.ERROR_500};
                  color: ${colors.ERROR_200};
                  background-color: rgba(51, 6, 6, 0.5);
                  font-size: ${isMobile ? '12px' : '14px'};
                  white-space: nowrap;
                  display: inline-block;
                  text-align: center;
                  min-width: 70px;
                  cursor: help;
                `}
              >
                Failed
              </span>
            </Tooltip>
          )
        }

        if (isPaidInPerson) {
          return (
            <Tooltip
              title={
                <div css={tooltipContentStyles}>
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.WHITE}
                  >
                    This payment was made in person. Refunds and receipts are
                    not available for in-person payments.
                  </Text>
                </div>
              }
              placement='top'
              zIndex={99999}
              trigger={['hover']}
              mouseEnterDelay={0.5}
              overlayStyle={tooltipOverlayStyle}
              overlayInnerStyle={tooltipInnerStyle}
            >
              <span
                css={css`
                  padding: 2px 4px;
                  border-radius: 4px;
                  border: 1px solid ${colors.INFO_500};
                  background-color: rgba(6, 22, 51, 0.5);
                  color: ${colors.INFO_200};
                  font-size: ${isMobile ? '12px' : '14px'};
                  white-space: nowrap;
                  display: inline-block;
                  text-align: center;
                  min-width: 70px;
                  cursor: help;
                `}
              >
                Paid (EXT)
              </span>
            </Tooltip>
          )
        }

        const statusChip = (
          <span
            css={css`
              padding: 2px 4px;
              border-radius: 4px;
              border: 1px solid
                ${isPaid
                  ? colors.SUCCESS_500
                  : isPastDue
                  ? colors.ERROR_500
                  : colors.WARNING_500};
              color: ${isPaid
                ? colors.SUCCESS_200
                : isPastDue
                ? colors.ERROR_200
                : colors.WARNING_200};
              background-color: ${isPaid
                ? 'rgba(5, 58, 17, 0.50)'
                : isPastDue
                ? 'rgba(51, 6, 6, 0.50)'
                : 'rgba(51, 32, 6, 0.50)'};
              font-size: ${isMobile ? '12px' : '14px'};
              white-space: nowrap;
              display: inline-block;
              text-align: center;
              min-width: 70px;
            `}
          >
            {isPaid ? 'Paid' : isPastDue ? 'Past Due' : 'Pending'}
          </span>
        )

        return (
          <Tooltip
            placement='top'
            zIndex={99999}
            trigger={['hover']}
            mouseEnterDelay={0.5}
          >
            {statusChip}
          </Tooltip>
        )
      },
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      width: '120px',
      align: 'right',
      sorter: (a, b) => dayjs(a.due_date).unix() - dayjs(b.due_date).unix(),
      render: (date: string, record: BillingScheduleItem) => (
        <span
          css={css`
            color: ${dayjs(date).isBefore(dayjs()) && !record.paid
              ? colors.ERROR_200
              : colors.WHITE};
          `}
        >
          {formatShortDate(date)}
        </span>
      ),
    },
    {
      title: 'Paid At',
      dataIndex: 'paid_at',
      key: 'paid_at',
      width: '120px',
      align: 'right',
      render: (date: string | null) => (
        <span>{date ? formatShortDate(date) : '-'}</span>
      ),
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      key: 'subtotal',
      align: 'right',
      width: '150px',
      sorter: (a, b) => a.subtotal - b.subtotal,
      render: (amount: number) => (
        <span>{formatCurrency(amount, currencyCode)}</span>
      ),
    },
    {
      title: 'Tax',
      dataIndex: 'taxtotal',
      key: 'taxtotal',
      align: 'right',
      width: '150px',
      sorter: (a, b) => a.taxtotal - b.taxtotal,
      render: (amount: number) => (
        <span>{formatCurrency(amount, currencyCode)}</span>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      width: '150px',
      sorter: (a, b) => a.total - b.total,
      render: (amount: number) => (
        <span>{formatCurrency(amount, currencyCode)}</span>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '300px',
      render: (_text: string, record: BillingScheduleItem) => {
        const fullText =
          record?.description || record?.charge?.description || ''
        return (
          <Tooltip title={fullText} placement='top' zIndex={12500}>
            <span
              css={css`
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
                text-overflow: ellipsis;
              `}
            >
              {fullText}
            </span>
          </Tooltip>
        )
      },
    },
  ]

  if (shouldDisplayBillingPayment) {
    cols.push({
      title: '',
      key: 'action',
      align: 'right',
      width: 150,
      fixed: 'right',
      render: (_value: unknown, record: BillingScheduleItem) => {
        const isPaidInPerson = record.paid && record.paid_in_person
        const isPaid = record?.paid
        // const showEmailButton = record.total > 0
        const showDownloadButton = isPaid && !isPaidInPerson

        return (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              justify-content: flex-end;
            `}
          >
            {!record.paid && (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  gap: 8px;
                  cursor: pointer;
                  border: 1px solid ${colors.PRIMARY};
                  padding: 4px 8px;
                  border-radius: 8px;
                  background-color: ${colors.PRIMARY};
                  min-width: ${isMobile ? '40px' : '100px'};
                `}
                onClick={() => handlePayment(record)}
              >
                {!isMobile && (
                  <span
                    css={css`
                      color: ${colors.SECONDARY_300};
                      font-size: 14px;
                    `}
                  >
                    Pay
                  </span>
                )}
                <DollarOutlined
                  css={css`
                    font-size: 16px;
                    color: ${colors.SECONDARY_300};
                  `}
                />
              </div>
            )}

            {showDownloadButton && (
              <Tooltip title='Download PDF' placement='top'>
                <button
                  type='button'
                  onClick={() => handleDownloadPdf(record.uid)}
                  css={css`
                    background: transparent;
                    border: none;
                    cursor: pointer;
                    padding: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    &:hover {
                      opacity: 0.8;
                    }
                  `}
                >
                  {pdfLoadingIds.includes(record.uid) ? (
                    <LoadingSpinner size={1.5} />
                  ) : (
                    <DownloadReceiptIcon />
                  )}
                </button>
              </Tooltip>
            )}
          </div>
        )
      },
    })
  }

  const columns: ColumnsType<BillingScheduleItem> = cols

  if (billingItems.length === 0) {
    return <p>No billing details</p>
  }

  return (
    <Table
      columns={columns}
      dataSource={billingItems}
      rowKey='uid'
      pagination={false}
      size='small'
      scroll={{ x: isMobile || width < 1200 ? 800 : undefined }}
      css={getTableStyles(isMobile)}
    />
  )
}

export default BillingTable
